import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TICKET_SIDEBAR_UPDATE } from "../../utils/ticketMessagesEvents";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            const current = state[ticketIndex];
            state[ticketIndex] = {
                ...current,
                ...ticket,
                contact: ticket.contact || current.contact,
                queue: ticket.queue || current.queue,
                tags: ticket.tags || current.tags,
                lastMessage: ticket.lastMessage || current.lastMessage,
            };
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const incoming = action.payload || {};
        const lastMessageFallback = action.lastMessage;
        const ticketIndex = state.findIndex(
            (t) =>
                (incoming.id && t.id === incoming.id) ||
                (incoming.uuid && t.uuid === incoming.uuid)
        );

        if (ticketIndex !== -1) {
            const current = state[ticketIndex];
            const merged = {
                ...current,
                ...incoming,
                contact: incoming.contact || current.contact,
                queue: incoming.queue || current.queue,
                tags: incoming.tags || current.tags,
                user: incoming.user || current.user,
                whatsapp: incoming.whatsapp || current.whatsapp,
                lastMessage:
                    incoming.lastMessage ||
                    lastMessageFallback ||
                    current.lastMessage,
                unreadMessages:
                    incoming.unreadMessages !== undefined &&
                    incoming.unreadMessages !== null
                        ? incoming.unreadMessages
                        : current.unreadMessages,
                updatedAt: incoming.updatedAt || new Date().toISOString(),
            };
            state[ticketIndex] = merged;
            if (action.moveToTop !== false) {
                state.unshift(state.splice(ticketIndex, 1)[0]);
            }
        } else if (!action.status || action.status === incoming.status) {
            if (!incoming.id) {
                return [...state];
            }
            state.unshift({
                ...incoming,
                lastMessage: incoming.lastMessage || lastMessageFallback,
                updatedAt: incoming.updatedAt || new Date().toISOString(),
            });
        }

        if (sortDir && ["ASC", "DESC"].includes(sortDir) && action.forceSort) {
            sortDir === "ASC"
                ? state.sort(ticketSortAsc)
                : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    const ticketsListRef = useRef([]);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === "enable" || user.allTicket === "enabled";
    const companyId = user.companyId;

    useEffect(() => {
        ticketsListRef.current = ticketsList;
    }, [ticketsList]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    useEffect(() => {
        if (!socket || !user?.companyId || typeof socket.on !== "function") {
            return undefined;
        }

        const queueIdSet = new Set(
            (selectedQueueIds || []).map((id) => Number(id))
        );

        const isAlreadyInList = (ticket) => {
            if (!ticket) return false;
            return ticketsListRef.current.some(
                (item) =>
                    (ticket.id && item.id === ticket.id) ||
                    (ticket.uuid && item.uuid === ticket.uuid)
            );
        };

        const shouldUpdateTicket = (ticket) => {
            if (!ticket) return false;
            if (isAlreadyInList(ticket)) return true;

            const belongsToUser =
                !ticket.userId || ticket.userId === user?.id || showAll;
            const inSelectedQueue =
                (!ticket.queueId && showTicketWithoutQueue) ||
                queueIdSet.has(Number(ticket.queueId));

            return belongsToUser && inSelectedQueue;
        };

        const notBelongsToUserQueues = (ticket) =>
            ticket?.queueId && !queueIdSet.has(Number(ticket.queueId));

        const patchTicketFromMessage = (data = {}) => {
            const ticket = data.ticket || data.message?.ticket;
            if (!ticket) return;
            if (
                status &&
                ticket.status &&
                ticket.status !== status &&
                !isAlreadyInList(ticket)
            ) {
                return;
            }
            if (!shouldUpdateTicket(ticket)) return;

            dispatch({
                type: "UPDATE_TICKET_UNREAD_MESSAGES",
                payload: ticket,
                lastMessage: data.lastMessage || data.message?.body,
                status,
                sortDir: sortTickets,
                moveToTop: true,
            });
        };

        const onCompanyTicketTicketsList = (data) => {
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets,
                });
            }
            if (
                data.action === "update" &&
                shouldUpdateTicket(data.ticket) &&
                data.ticket.status === status
            ) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets,
                });
            }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data.ticket?.id,
                    status: status,
                    sortDir: sortTickets,
                });
            }
            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data?.ticketId,
                    status: status,
                    sortDir: sortTickets,
                });
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action !== "create") return;
            patchTicketFromMessage(data);
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets,
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        };

        const onSidebarUpdate = (event) => {
            const detail = event.detail || {};
            const ticket = detail.ticket || {
                id: detail.ticketId,
                uuid: detail.ticketUuid,
                lastMessage: detail.lastMessage,
                status: detail.status || status,
                unreadMessages: detail.fromMe ? 0 : undefined,
                updatedAt: new Date().toISOString(),
            };

            if (status && ticket.status && ticket.status !== status) {
                if (!isAlreadyInList(ticket)) return;
            }

            patchTicketFromMessage({
                ticket,
                lastMessage: detail.lastMessage,
                message: detail.message,
            });
        };

        const ticketEventName = `company-${companyId}-ticket`;
        const appMessageEventName = `company-${companyId}-appMessage`;
        const contactEventName = `company-${companyId}-contact`;

        socket.on("connect", onConnectTicketsList);
        socket.on(ticketEventName, onCompanyTicketTicketsList);
        socket.on(appMessageEventName, onCompanyAppMessageTicketsList);
        socket.on(contactEventName, onCompanyContactTicketsList);
        window.addEventListener(TICKET_SIDEBAR_UPDATE, onSidebarUpdate);

        if (socket.connected || socket.socket?.connected) {
            onConnectTicketsList();
        }

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(ticketEventName, onCompanyTicketTicketsList);
            socket.off(appMessageEventName, onCompanyAppMessageTicketsList);
            socket.off(contactEventName, onCompanyContactTicketsList);
            window.removeEventListener(TICKET_SIDEBAR_UPDATE, onSidebarUpdate);
        };
    }, [
        status,
        showAll,
        selectedQueueIds,
        sortTickets,
        showTicketWithoutQueue,
        socket,
        companyId,
        user?.id,
        user?.companyId,
    ]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                />
                            ))}
                        </>
                    )}
                    {loading && ticketsList.length === 0 && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
